/**
 * Checkout Store
 * Phase 1: URL Parameter Handling
 * Phase 2: Availability Check
 * Phase 3: NBC Product Data Fetching
 * Phase 4: Traveler Data Handling
 * Phase 5: Booking Submission
 *
 * Manages checkout state from URL parameters with validation and availability verification.
 * No URL synchronization back to browser (read-only from URL).
 * No localStorage persistence (checkout is session-specific).
 */

import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';
import type {
  CheckoutState,
  Passenger,
  CheckoutValidationResult,
  MultiRoomAvailabilityCheckResult,
  RoomCombination,
  BookingSubmitResult,
} from '@/types/checkout';
import type { RoomOccupancy } from '@/types/SearchBar';
import type { CheckoutFormData } from '@/types/schemas/checkout';
import type { Traveler } from '@/types/Traveler';
import { $t } from '@/composables/translations/useTranslate';
import {
  checkMultiRoomAvailability,
  isFixedBooking,
  isRequestBookingRequired,
  isConfirmationRequired,
  isAvailableCode,
} from '@/services/api/davinci/checkAvailability';
import type { BookingFlowType } from '@/constants/davinci/returnCodes';
import { submitBooking as submitBookingApi } from '@/services/api/davinci';
import {
  deriveAgencyNumberFromTravelType,
  isKnownTravelTypePrefix,
} from '@/services/api/davinci/helpers';
import { fireBandProbe } from '@/services/api/davinci/bandProbe';
import { FALLBACK_BANDS, deriveBands, type AgeBands } from '@/types/schemas/checkout/ageBands';
import { DaVinciApiError, DaVinciNetworkError } from '@/types/errors';
import { getProduct } from '@/services/api/nbc';
import type { NBCSingleProductResponse } from '@/types/schemas/nbc';
import type { PassengerInfo } from '@/types/schemas/player/common';
import { extractCatalogCodeFromBookingResponse } from '@/utils/checkout/catalogCodeExtractor';
import { formatDateLocal } from '@/utils/formatting/dateFormatter';
import { classifyPassenger } from '@/utils/checkout/classification';
import { getInsurances } from '@/services/api/ssc';
import { buildSscInsurancesRequest, type SelectedExtra } from '@/utils/ssc/sscRequestBuilder';
import type { SscInsurance } from '@/types/schemas/ssc';
import { getInsuranceTotalPrice } from '@/types/schemas/ssc';
import { getExtras } from '@/services/api/pps';
import { buildPpsExtrasRequest } from '@/utils/pps/ppsRequestBuilder';
import type { PpsService } from '@/types/schemas/pps';
import { extractExtrasFromResponse } from '@/types/schemas/pps';
import { parseAuxiliaryAvailability } from '@/constants/extras/availability';
import { HIDE_AUXILIARIES_STEP } from '@/constants/checkout/config';
import {
  extractPricingFromBookingResponse,
  hasPricingData,
  validatePricingConsistency,
  type SeparateItem,
  type AuxService,
  type RebateSummaryLine,
} from '@/utils/checkout/pricingExtractor';
import type { ExtractedDiscount } from '@/types/schemas/checkout/discount';
import {
  extractDetailsFromBookingResponse,
  bookingResponseHasPackageRequirement,
} from '@/utils/checkout/bookingDetailsExtractor';
import { extractAttachmentsFromBookingResponse } from '@/utils/checkout/attachmentsExtractor';
import {
  extractOccupationConstraints,
  type OccupationConstraints,
} from '@/utils/checkout/occupationInfoExtractor';
import {
  extractPaymentScheduleFromBookingResponse,
  type PaymentSchedule,
} from '@/utils/checkout/paymentScheduleExtractor';

export const useCheckoutStore = defineStore('checkout', () => {
  // Basic State
  const activeStep = ref(HIDE_AUXILIARIES_STEP ? 1 : 0);
  const activeSubStep = ref(0);
  const offerIds = ref<number[] | null>(null);
  const roomCodes = ref<string[] | null>(null);
  const serviceVariantIds = ref<number[] | null>(null);
  const productCode = ref<string | null>(null);
  const boardCode = ref<string | null>(null);
  const checkIn = ref<Date | null>(null);
  const checkOut = ref<Date | null>(null);
  const passengers = ref<Passenger[] | null>(null);
  const roomOccupancy = ref<RoomOccupancy | null>(null);

  // Availability check
  const multiRoomAvailabilityResult = ref<MultiRoomAvailabilityCheckResult | null>(null);
  const flowBeforeConfirmation = ref<Exclude<BookingFlowType, 'confirmation'> | null>(null);
  const isCheckingAvailability = ref(false);
  const availabilityError = ref<string | null>(null);
  const sessionId = ref<string | null>(null);
  const activityId = ref<string | null>(null);
  const travelType = ref<string | null>(null);

  // Age bands — the hotel's Adult/Child/Infant boundaries, derived from the band probe
  // (a throwaway 670 BA). Non-nullable: defaults to FALLBACK_BANDS so the form schema
  // factory never sees null even if the probe is still running or fails.
  const ageBands = ref<AgeBands>(FALLBACK_BANDS);
  const isLoadingAgeBands = ref(false);
  const popupHints = ref<string[] | null>(null);
  const agencyName = ref<string | null>(null);
  const tourOperator = ref<string | null>(null);
  const mealDescription = ref<string | null>(null);

  // Per-service occupancy constraints from the successful BA #1 (OccupationInfo). Captured
  // for readiness; party validation against them is deferred (framework §9.7).
  // TODO: party validation (framework §9.7).
  const occupationConstraints = ref<OccupationConstraints[]>([]);

  // ExternalAttachments URLs from DaVinci BA response (statutory documents)
  const vviUrl = ref<string | null>(null);

  const isPackageTrip = ref(false);

  // Pricing Data (extracted from DaVinci)
  const servicePrices = ref<Map<number, number>>(new Map());
  const totalBookingPrice = ref<number>(0);
  const roomOnlyPrices = ref<Map<number, number>>(new Map());
  const boardNames = ref<Map<number, string>>(new Map());
  const boardDescriptions = ref<Map<number, string>>(new Map());
  const separateItems = ref<Map<number, SeparateItem[]>>(new Map());
  const auxServices = ref<AuxService[]>([]);
  const accommodationTotal = ref<number>(0);
  const discounts = ref<ExtractedDiscount[]>([]);
  const totalDiscount = ref<number>(0);
  const originalPrice = ref<number>(0);
  const rebateSummary = ref<RebateSummaryLine[]>([]);
  const discountPercent = ref<number | undefined>(undefined);

  // Phase 2.5: SSC Insurance Data
  const catalogCode = ref<string | null>(null);
  const sscInsurances = ref<SscInsurance[]>([]);
  const isLoadingSsc = ref(false);
  const sscError = ref<string | null>(null);
  const selectedInsuranceId = ref<string | null>(null);

  // Phase 2.5: PPS Extras Data
  const ppsExtras = ref<PpsService[]>([]);
  const isLoadingPps = ref(false);
  const ppsError = ref<string | null>(null);
  const selectedExtrasMap = ref<Map<number, string>>(new Map());

  // NBC Product Data
  const nbcProductData = ref<NBCSingleProductResponse | null>(null);
  const isLoadingNbc = ref(false);
  const nbcError = ref<string | null>(null);

  // Booking Submission
  const travelerData = ref<CheckoutFormData | null>(null);
  const bookingResult = ref<BookingSubmitResult | null>(null);
  const isSubmittingBooking = ref(false);
  const bookingError = ref<string | null>(null);

  // Debug mode
  const isDebugMode = ref(false);

  // Computed: validation
  const isMultiRoom = computed(() => {
    return (offerIds.value?.length ?? 0) > 1;
  });

  const totalRooms = computed(() => {
    return offerIds.value?.length ?? 0;
  });

  const hasProductId = computed(() => offerIds.value !== null && offerIds.value.length > 0);
  const hasProductCode = computed(() => productCode.value !== null);
  const hasRoomCode = computed(() => roomCodes.value !== null && roomCodes.value.length > 0);
  const hasBoardCode = computed(() => boardCode.value !== null);
  const hasServiceVariantId = computed(
    () => serviceVariantIds.value !== null && serviceVariantIds.value.length > 0
  );
  const hasCheckIn = computed(() => checkIn.value !== null);
  const hasCheckOut = computed(() => checkOut.value !== null);
  const hasPassengers = computed(() => passengers.value !== null && passengers.value.length > 0);

  const hasValidDateRange = computed(() => {
    if (!checkIn.value || !checkOut.value) return false;
    return checkOut.value > checkIn.value;
  });

  const hasAllRequiredParams = computed(() => {
    const baseValid =
      hasProductId.value &&
      hasProductCode.value &&
      hasRoomCode.value &&
      hasBoardCode.value &&
      hasServiceVariantId.value &&
      hasCheckIn.value &&
      hasCheckOut.value &&
      hasPassengers.value;

    if (baseValid && offerIds.value && roomCodes.value && serviceVariantIds.value) {
      return (
        offerIds.value.length === roomCodes.value.length &&
        offerIds.value.length === serviceVariantIds.value.length
      );
    }

    return baseValid;
  });

  const missingRequiredFields = computed(() => {
    const missing: string[] = [];
    if (!hasProductId.value) missing.push('offerIds');
    if (!hasProductCode.value) missing.push('productCode');
    if (!hasRoomCode.value) missing.push('roomCodes');
    if (!hasBoardCode.value) missing.push('boardCode');
    if (!hasServiceVariantId.value) missing.push('serviceVariantIds');
    if (!hasCheckIn.value) missing.push('checkIn');
    if (!hasCheckOut.value) missing.push('checkOut');
    if (!hasPassengers.value) missing.push('pax');

    // Check array length consistency
    if (
      offerIds.value &&
      roomCodes.value &&
      serviceVariantIds.value &&
      (offerIds.value.length !== roomCodes.value.length ||
        offerIds.value.length !== serviceVariantIds.value.length)
    ) {
      missing.push('array-length-mismatch');
    }

    return missing;
  });

  /**
   * Room numbers (1-based) that contain no traveler aged 18+. Always empty for a booking
   * built through the search — `RoomSelector` requires ≥1 adult per room — so this only
   * trips on a hand-edited checkout URL (e.g. `pax=r16-8r35-35`). A booking legally needs
   * an adult (the contact/invoice recipient must be 18+, see contact.ts), and the
   * participant→room mapping assumes each room is led by its adult, so we fail fast rather
   * than mis-slot the contact. An under-18 hotel-adult (e.g. `16a`) does NOT satisfy this:
   * `isAdult` is the ≥18 binary flag. See claude/specs/ChildVsInfant.
   */
  const roomsMissingAdult = computed<number[]>(() => {
    if (!passengers.value || passengers.value.length === 0) return [];

    const hasAdultByRoom = new Map<number, boolean>();
    for (const passenger of passengers.value) {
      const room = passenger.roomNumber ?? 1;
      hasAdultByRoom.set(room, (hasAdultByRoom.get(room) ?? false) || passenger.isAdult);
    }

    return Array.from(hasAdultByRoom.entries())
      .filter(([, hasAdult]) => !hasAdult)
      .map(([room]) => room)
      .sort((a, b) => a - b);
  });

  const firstMissingParam = computed(() => {
    if (!hasProductId.value) return 'offerIds';
    if (!hasProductCode.value) return 'productCode';
    if (!hasCheckIn.value) return 'checkIn';
    if (!hasCheckOut.value) return 'checkOut';
    if (!hasPassengers.value) return 'pax';
    if (!hasRoomCode.value) return 'roomCodes';
    if (!hasBoardCode.value) return 'boardCode';
    if (!hasServiceVariantId.value) return 'serviceVariantIds';
    return null;
  });

  // Computed: Passenger Counts
  // Counts are hotelClass-aware (the hotel's Adult/Child/Infant label) so a 16yo
  // hotel-adult counts as an adult and an infant is not lumped in with children.
  // Plain/bookmarked URLs carry no hotelClass → fall back to the binary isAdult/isChild.
  const adultCount = computed(() => {
    if (!passengers.value) return 0;
    return passengers.value.filter((p) => classifyPassenger(p) === 'adult').length;
  });

  const childCount = computed(() => {
    if (!passengers.value) return 0;
    return passengers.value.filter((p) => classifyPassenger(p) === 'child').length;
  });

  const infantCount = computed(() => {
    if (!passengers.value) return 0;
    return passengers.value.filter((p) => classifyPassenger(p) === 'infant').length;
  });

  const totalPassengers = computed(() => {
    if (!passengers.value) return 0;
    return passengers.value.length;
  });

  // Computed: Formatted Display
  const checkInFormatted = computed(() => {
    if (!checkIn.value) return '';
    const d = checkIn.value;
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear().toString().slice(-2);
    return `${day}.${month}.${year}`;
  });

  const checkOutFormatted = computed(() => {
    if (!checkOut.value) return '';
    const d = checkOut.value;
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear().toString().slice(-2);
    return `${day}.${month}.${year}`;
  });

  const nightCount = computed(() => {
    if (!checkIn.value || !checkOut.value) return 0;
    const diffTime = Math.abs(checkOut.value.getTime() - checkIn.value.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  });

  // Computed: Room Combination (Multi-Room)
  /**
   * Build RoomCombination from current state
   * Used for multi-room availability checks and display
   */
  const roomCombination = computed((): RoomCombination | null => {
    if (
      !hasAllRequiredParams.value ||
      !offerIds.value ||
      !roomCodes.value ||
      !serviceVariantIds.value ||
      !passengers.value ||
      !checkIn.value ||
      !checkOut.value ||
      // null = board code missing (block); '' = intentional no-meal (allow)
      boardCode.value === null
    ) {
      return null;
    }

    const allPassengers: PassengerInfo[] = [];
    passengers.value.forEach((p, index) => {
      allPassengers.push({
        id: index + 1, // Sequential: 1, 2, 3, 4, ...
        type: p.isAdult ? ('adult' as const) : ('child' as const),
        age: p.age,
        hotelClass: p.hotelClass, // 3-way hotel label (undefined for plain URLs)
      });
    });

    // Map each offer to a MappedOffer with room assignment
    let globalPassengerIndex = 0;
    const products = offerIds.value.map((offerId, index) => {
      const roomPassengers = passengers.value!.filter((p) => p.roomNumber === index + 1);

      const passengerInfo: PassengerInfo[] = roomPassengers.map(() => {
        const passengerData = allPassengers[globalPassengerIndex];
        globalPassengerIndex++;
        return passengerData;
      });

      const serviceIndex = index + 1;
      const price =
        roomOnlyPrices.value.get(serviceIndex) || servicePrices.value.get(serviceIndex) || 0;

      return {
        offerId,
        serviceId: 0, // Not used in checkout
        serviceVariantId: serviceVariantIds.value![index],
        // Local calendar day, NOT toISOString(): checkIn is a local-midnight Date, so
        // toISOString() lands on the previous day for CET/CEST and toUtcMidnight()
        // downstream would book one day early (VAM-258 bug class).
        startDate: formatDateLocal(checkIn.value!),
        endDate: formatDateLocal(checkOut.value!),
        roomCode: roomCodes.value![index],
        roomDescription: roomCodes.value![index],
        roomTypes: [],
        boardCode: boardCode.value!,
        boardType: boardCode.value!,
        boardName: boardCode.value!,
        price: price,
        pricePerPerson: false,
        currency: 'EUR',
        requestBooking: false,
        availability: 'unknown' as const,
        passengerIds: passengerInfo.map((p) => p.id),
        passengers: passengerInfo,
        discount: undefined,
        discountReason: undefined,
        oldPrice: undefined,
      };
    });

    return {
      products,
      totalPrice: totalBookingPrice.value,
      currency: 'EUR',
      boardCode: boardCode.value,
      boardType: boardCode.value,
      boardName: boardCode.value,
      startDate: formatDateLocal(checkIn.value),
      endDate: formatDateLocal(checkOut.value),
      roomDescriptions: roomCodes.value,
      roomCodes: roomCodes.value,
      passengerCounts: products.map((p) => p.passengers.length),
      availability: 'unknown' as const,
      allPassengers,
    };
  });

  // Computed: Phase 2 Availability
  const hasCheckedAvailability = computed(() => multiRoomAvailabilityResult.value !== null);
  const isAvailable = computed(() => {
    if (!multiRoomAvailabilityResult.value) return false;
    return multiRoomAvailabilityResult.value.available;
  });
  const availabilityCode = computed(() => multiRoomAvailabilityResult.value?.code ?? null);
  const availabilityMessage = computed(() => multiRoomAvailabilityResult.value?.message ?? null);

  const agencyNumber = computed<number | null>(() =>
    travelType.value === null ? null : deriveAgencyNumberFromTravelType(travelType.value)
  );

  const agencyNumberIsFallback = computed(
    () => !!travelType.value?.trim() && !isKnownTravelTypePrefix(travelType.value)
  );

  const hasOnRequestAuxiliarySelected = computed((): boolean => {
    if (multiRoomAvailabilityResult.value?.baStage === 'preSubmit') return false;

    const insurance = selectedInsurance.value;
    if (insurance) {
      const raw = insurance.ServiceTypes[0]?.Availability ?? '';
      if (parseAuxiliaryAvailability(raw) === 'on-request') return true;
    }
    for (const [bookingCodeId, serviceTypeCode] of selectedExtrasMap.value.entries()) {
      const service = ppsExtras.value.find((e) => e.BookingCodeId === bookingCodeId);
      const st = service?.ServiceTypes?.find((t) => t.Code === serviceTypeCode);
      if (st && parseAuxiliaryAvailability(st.Availability) === 'on-request') return true;
    }
    return false;
  });

  const requiresRequestBooking = computed(() => {
    if (hasOnRequestAuxiliarySelected.value) return true;
    if (!availabilityCode.value) return false;
    return isRequestBookingRequired(availabilityCode.value);
  });
  const requiresConfirmation = computed(() => {
    if (hasOnRequestAuxiliarySelected.value) return false;
    if (!availabilityCode.value) return false;
    return isConfirmationRequired(availabilityCode.value);
  });
  const isFixedBookingType = computed(() => {
    if (hasOnRequestAuxiliarySelected.value) return false;
    if (!availabilityCode.value) return false;
    return isFixedBooking(availabilityCode.value);
  });

  // BC is a technical follow-up to B or BQ. For payment-step UX we collapse it
  // back to the originating flow: BC←B behaves like B, BC←BQ behaves like BQ.
  // BC as initial flow falls back to 'fixed' (per product requirement).
  const effectivePaymentFlow = computed<Exclude<BookingFlowType, 'confirmation'>>(() => {
    if (requiresRequestBooking.value) return 'request';
    if (requiresConfirmation.value) {
      return flowBeforeConfirmation.value === 'request' ? 'request' : 'fixed';
    }
    return 'fixed';
  });
  const isPaymentMethodVisible = computed(() => effectivePaymentFlow.value === 'fixed');
  const isRequestSubmitCta = computed(() => effectivePaymentFlow.value === 'request');

  // Snapshot the effective flow into flowBeforeConfirmation right before
  // multiRoomAvailabilityResult is mutated. Must run BEFORE the assignment so
  // computeds still reflect the previous state.
  function captureFlowBeforeConfirmation(newCode: number | null): void {
    const newIsBC = newCode != null && isConfirmationRequired(newCode);
    if (!newIsBC) {
      flowBeforeConfirmation.value = null;
      return;
    }
    // Already inside BC from a prior call — keep the originally captured origin.
    if (requiresConfirmation.value) return;

    if (requiresRequestBooking.value) {
      flowBeforeConfirmation.value = 'request';
    } else if (isFixedBookingType.value) {
      flowBeforeConfirmation.value = 'fixed';
    } else {
      flowBeforeConfirmation.value = null;
    }
  }

  const canProceedToNextStep = computed(() => {
    return hasAllRequiredParams.value && hasCheckedAvailability.value && isAvailable.value;
  });

  const hasDiscount = computed(() => totalDiscount.value < 0);

  /** Named rebates for the badge tooltip; unnamed rebate lines are left out. */
  const discountReasons = computed(() =>
    rebateSummary.value.map((line) => line.label).filter((label) => label !== '')
  );

  /**
   * Deposit / balance split for this booking, or null when it is payable in one
   * amount (the common case — DaVinci only splits for departures ~44+ days out).
   *
   * Derived straight from the retained BA response rather than pushed into a ref
   * by the extractors, because `setInitialState` restores
   * `multiRoomAvailabilityResult` wholesale WITHOUT re-running them — a ref would
   * silently go null on back-navigation into the payment step.
   */
  const paymentSchedule = computed<PaymentSchedule | null>(() => {
    const bookingResponse = multiRoomAvailabilityResult.value?.bookingResponse;
    if (!bookingResponse) return null;
    return extractPaymentScheduleFromBookingResponse(bookingResponse);
  });

  const isVamosTourOperator = computed(() => {
    if (!tourOperator.value) return true;
    return tourOperator.value.toUpperCase() === 'VEKR';
  });

  // Computed: Phase 3 - NBC Data
  const hasNbcData = computed(() => nbcProductData.value !== null);
  const productHighlights = computed(() => {
    return nbcProductData.value?.highlights ?? null;
  });
  const productDescription = computed(() => {
    return nbcProductData.value?.description ?? null;
  });
  const productName = computed(() => {
    return nbcProductData.value?.name ?? productCode.value ?? null;
  });
  const destinationName = computed(() => {
    return nbcProductData.value?.destinationName ?? null;
  });
  const mainProductImage = computed(() => {
    return nbcProductData.value?.mainImage ?? null;
  });
  const productDetailUrl = computed(() => {
    return nbcProductData.value?.pdpUrl ?? null;
  });
  const providerTermsLink = computed(() => {
    return nbcProductData.value?.providerTermsLink ?? null;
  });
  const cancellationTermsLink = computed(() => {
    return nbcProductData.value?.cancellationTerms ?? null;
  });

  const isPackage = computed(() => isPackageTrip.value);

  const isStatutoryCheckboxVisible = computed(() => {
    return isPackage.value || vviUrl.value !== null;
  });

  // Actions: State Management
  function setInitialState(state: CheckoutState): void {
    // Phase 1: Booking Data
    offerIds.value = state.offerIds;
    roomCodes.value = state.roomCodes;
    serviceVariantIds.value = state.serviceVariantIds;
    productCode.value = state.productCode;
    boardCode.value = state.boardCode;
    checkIn.value = state.checkIn;
    checkOut.value = state.checkOut;
    passengers.value = state.passengers;
    roomOccupancy.value = state.roomOccupancy;

    // Phase 2: Availability
    captureFlowBeforeConfirmation(state.multiRoomAvailabilityResult?.code ?? null);
    multiRoomAvailabilityResult.value = state.multiRoomAvailabilityResult;
    isCheckingAvailability.value = state.isCheckingAvailability;
    availabilityError.value = state.availabilityError;
    sessionId.value = state.sessionId;
    activityId.value = state.activityId;

    const restoredBooking = state.multiRoomAvailabilityResult?.bookingResponse;
    isPackageTrip.value = restoredBooking
      ? bookingResponseHasPackageRequirement(restoredBooking)
      : false;

    // Phase 3: NBC
    nbcProductData.value = state.nbcProductData;
    isLoadingNbc.value = state.isLoadingNbc;
    nbcError.value = state.nbcError;

    // UI state
    activeStep.value = state.activeStep;
    activeSubStep.value = 0;
    mealDescription.value = state.mealDescription;
    popupHints.value = state.popupHints;

    // Debug mode
    isDebugMode.value = state.isDebugMode;
  }

  function setActiveStep(step: number): void {
    if (step >= 0 && step <= 3) {
      activeStep.value = step;
      activeSubStep.value = 0;
    }
  }

  function setActiveSubStep(subStep: number): void {
    activeSubStep.value = subStep;
  }

  /**
   * Clear all checkout data
   */
  function clearCheckout(): void {
    offerIds.value = null;
    roomCodes.value = null;
    serviceVariantIds.value = null;
    productCode.value = null;
    boardCode.value = null;
    checkIn.value = null;
    checkOut.value = null;
    passengers.value = null;
    roomOccupancy.value = null;
    activeStep.value = 0;
    activeSubStep.value = 0;
    mealDescription.value = null;

    clearAvailability();
    clearNbcData();
    clearSscData();
    clearPpsData();
    clearTravelerData();
    clearBookingResult();
  }

  // Actions: Validation
  function validateCheckout(): CheckoutValidationResult {
    const errorMessages: string[] = [];

    if (!hasAllRequiredParams.value) {
      missingRequiredFields.value.forEach((field) => {
        errorMessages.push(getErrorMessageForParam(field));
      });
    }

    if (hasCheckIn.value && hasCheckOut.value && !hasValidDateRange.value) {
      errorMessages.push(
        $t('ibe.checkout.error.invalid-date-range', 'Check-out date must be after check-in date')
      );
    }

    // Each room needs a traveler aged 18+ (the booker must be an adult). Only a
    // hand-edited URL can violate this; fail fast instead of mis-mapping participants.
    if (roomsMissingAdult.value.length > 0) {
      errorMessages.push(
        $t(
          'ibe.checkout.error.room-needs-adult',
          'Each room requires at least one traveler aged 18 or older.'
        )
      );
    }

    return {
      isValid: errorMessages.length === 0,
      errors: errorMessages,
      missingFields: missingRequiredFields.value,
    };
  }

  function getErrorMessageForParam(paramName: string): string {
    const ERROR_MESSAGE_KEYS: Record<string, string> = {
      offerIds: 'ibe.checkout.error.missing-product-id',
      productCode: 'ibe.checkout.error.missing-product-code',
      roomCodes: 'ibe.checkout.error.missing-room-code',
      boardCode: 'ibe.checkout.error.missing-board-code',
      serviceVariantIds: 'ibe.checkout.error.missing-service-variant',
      checkIn: 'ibe.checkout.error.missing-check-in',
      checkOut: 'ibe.checkout.error.missing-check-out',
      pax: 'ibe.checkout.error.missing-pax',
    };

    const key = ERROR_MESSAGE_KEYS[paramName];
    if (!key) {
      return $t('ibe.checkout.error.missing-parameter', 'Parameter missing');
    }
    return $t(key, 'Missing: {param}', { param: paramName });
  }

  // Actions: Phase 2 Availability Check
  async function checkProductAvailability(): Promise<void> {
    const combination = roomCombination.value;
    if (!combination || !productCode.value) {
      availabilityError.value = $t(
        'ibe.checkout.availability.error.missing-params',
        'Cannot check availability: missing required parameters'
      );
      return;
    }

    availabilityError.value = null;
    isCheckingAvailability.value = true;

    try {
      const result = await checkMultiRoomAvailability({
        combination,
        productCode: productCode.value,
        travelType: '',
      });

      captureFlowBeforeConfirmation(result.code);
      multiRoomAvailabilityResult.value = result;

      sessionId.value = result.sessionId ?? null;
      activityId.value = result.activityId ?? null;

      travelType.value = (result.bookingResponse?.['TravelType'] as string | undefined) ?? null;

      if (result.available && result.bookingResponse) {
        extractAndStorePricing(result.bookingResponse);
        extractAndStoreDetails(result.bookingResponse);
      }

      if (!result.available) {
        availabilityError.value = $t(
          'ibe.checkout.availability.error.unavailable',
          result.message || 'This offer is not available'
        );
      }
    } catch (error) {
      if (error instanceof DaVinciNetworkError) {
        if (error.isTimeout) {
          availabilityError.value = $t(
            'ibe.checkout.availability.error.timeout',
            'The availability check timed out. Please try again.'
          );
        } else {
          availabilityError.value = $t(
            'ibe.checkout.availability.error.network',
            'Network error while checking availability. Please check your connection and try again.'
          );
        }
      } else if (error instanceof DaVinciApiError) {
        availabilityError.value = $t(
          'ibe.checkout.availability.error.api',
          error.message || 'An error occurred while checking availability'
        );
      } else {
        availabilityError.value = $t(
          'ibe.checkout.availability.error.unknown',
          'An unexpected error occurred. Please try again.'
        );
        console.error('Unexpected error during multi-room availability check:', error);
      }

      const combinationId = combination.products.map((p) => p.offerId).join('-');
      captureFlowBeforeConfirmation(999);
      multiRoomAvailabilityResult.value = {
        code: 999,
        message: availabilityError.value,
        available: false,
        combinationId,
      };
    } finally {
      isCheckingAvailability.value = false;
    }
  }

  /**
   * Fire the band probe (spec §7) and store the resulting age bands.
   *
   * Runs in parallel with BA #1 on mount (independent of its session). The probe forces a
   * 670 to read the hotel's child band; on any failure (no band / 712 / error) we keep
   * FALLBACK_BANDS. Never throws — bands always end up non-null. The probe's session is
   * discarded inside fireBandProbe and never touches the real booking.
   */
  async function fetchAgeBands(): Promise<void> {
    const combination = roomCombination.value;
    if (!combination || !productCode.value) return;

    isLoadingAgeBands.value = true;
    try {
      const raw = await fireBandProbe(combination, productCode.value);
      ageBands.value = raw ? deriveBands(raw) : FALLBACK_BANDS;
    } catch {
      ageBands.value = FALLBACK_BANDS;
    } finally {
      isLoadingAgeBands.value = false;
    }
  }

  async function retryAvailabilityCheck(): Promise<void> {
    clearAvailability();
    clearSscData();
    clearPpsData();
    // Re-run the band probe alongside BA#1 (mirrors onMounted) — clearAvailability reset
    // ageBands to FALLBACK_BANDS, so without this the retried form would silently use the
    // fallback band and a valid child could fail BA#2 with a 670.
    await Promise.all([checkProductAvailability(), fetchAgeBands()]);

    // Re-fetch SSC and PPS after successful retry (mirrors onMounted logic)
    if (isAvailable.value) {
      await Promise.all([fetchSscInsurances(), fetchPpsExtras()]);
    }
  }

  function clearAvailability(): void {
    multiRoomAvailabilityResult.value = null;
    flowBeforeConfirmation.value = null;
    availabilityError.value = null;
    sessionId.value = null;
    activityId.value = null;
    travelType.value = null;
    isCheckingAvailability.value = false;
    servicePrices.value = new Map();
    totalBookingPrice.value = 0;
    roomOnlyPrices.value = new Map();
    boardNames.value = new Map();
    boardDescriptions.value = new Map();
    separateItems.value = new Map();
    auxServices.value = [];
    accommodationTotal.value = 0;
    mealDescription.value = null;
    popupHints.value = null;
    agencyName.value = null;
    tourOperator.value = null;
    vviUrl.value = null;
    isPackageTrip.value = false;
    discounts.value = [];
    totalDiscount.value = 0;
    originalPrice.value = 0;
    rebateSummary.value = [];
    discountPercent.value = undefined;
    catalogCode.value = null;
    occupationConstraints.value = [];
    ageBands.value = FALLBACK_BANDS;
    isLoadingAgeBands.value = false;
  }

  function extractAndStorePricing(bookingResponse: Record<string, unknown>): void {
    try {
      if (!hasPricingData(bookingResponse)) {
        console.warn('[Checkout Pricing] No pricing data found in booking response');
        return;
      }

      const extracted = extractPricingFromBookingResponse(bookingResponse);

      if (extracted.servicePrices.size === 0 && extracted.totalPrice === 0) {
        console.warn('[Checkout Pricing] Extracted pricing is empty');
        return;
      }

      // Validate consistency (optional - for debugging)
      if (extracted.servicePrices.size > 0) {
        validatePricingConsistency(extracted);
      }

      servicePrices.value = extracted.servicePrices;
      totalBookingPrice.value = extracted.totalPrice;
      roomOnlyPrices.value = extracted.roomOnlyPrices;
      boardNames.value = extracted.boardNames;
      boardDescriptions.value = extracted.boardDescriptions;
      separateItems.value = extracted.separateItems;
      auxServices.value = extracted.auxServices;
      accommodationTotal.value = extracted.accommodationTotal;
      discounts.value = extracted.discounts;
      totalDiscount.value = extracted.totalDiscount;
      originalPrice.value = extracted.originalPrice;
      rebateSummary.value = extracted.rebateSummary;
      discountPercent.value = extracted.discountPercent;
    } catch (error) {
      console.error('[Checkout Pricing] Failed to extract pricing:', error);
      servicePrices.value = new Map();
      totalBookingPrice.value = 0;
      roomOnlyPrices.value = new Map();
      boardNames.value = new Map();
      boardDescriptions.value = new Map();
      separateItems.value = new Map();
      auxServices.value = [];
      accommodationTotal.value = 0;
      discounts.value = [];
      totalDiscount.value = 0;
      originalPrice.value = 0;
      rebateSummary.value = [];
      discountPercent.value = undefined;
    }
  }

  function extractAndStoreDetails(bookingResponse: Record<string, unknown>): void {
    try {
      const extracted = extractDetailsFromBookingResponse(bookingResponse);

      mealDescription.value = extracted.mealDescription;
      popupHints.value = extracted.popupHints;
      agencyName.value = extracted.agencyName;
      tourOperator.value = extracted.tourOperator;

      const attachments = extractAttachmentsFromBookingResponse(bookingResponse);
      if (attachments.vviUrl !== null) vviUrl.value = attachments.vviUrl;

      if (bookingResponseHasPackageRequirement(bookingResponse)) isPackageTrip.value = true;

      // Extract catalogCode for SSC request (Phase 2.5)
      const extractedCatalogCode = extractCatalogCodeFromBookingResponse(bookingResponse);
      if (extractedCatalogCode) {
        catalogCode.value = extractedCatalogCode;
      }

      // Capture per-service occupancy constraints (deferred use — framework §9.7).
      occupationConstraints.value = extractOccupationConstraints(bookingResponse);
    } catch (error) {
      console.error('[Checkout Details] Failed to extract details:', error);
      mealDescription.value = null;
      popupHints.value = null;
      agencyName.value = null;
      tourOperator.value = null;
    }
  }

  // Actions: Phase 3 - NBC Data Fetching
  async function fetchNbcProductData(): Promise<void> {
    if (!productCode.value) {
      console.warn('[Checkout NBC] Cannot fetch: no product code available');
      return;
    }

    nbcError.value = null;
    isLoadingNbc.value = true;

    try {
      const result = await getProduct(productCode.value);

      nbcProductData.value = result;
    } catch (error) {
      console.warn('[Checkout NBC] Failed to fetch product data:', productCode.value, error);

      nbcError.value = error instanceof Error ? error.message : 'Failed to load product details';
      nbcProductData.value = null;

      // Don't throw - checkout continues without NBC enrichment
    } finally {
      isLoadingNbc.value = false;
    }
  }

  function clearNbcData(): void {
    nbcProductData.value = null;
    isLoadingNbc.value = false;
    nbcError.value = null;
  }

  // Actions: Phase 2.5 - SSC Insurance Data
  const hasSscData = computed(() => sscInsurances.value.length > 0);

  const selectedInsurance = computed(() => {
    if (!selectedInsuranceId.value) return null;
    return sscInsurances.value.find((i) => i.BookingCode === selectedInsuranceId.value) ?? null;
  });

  const selectedInsurancePrice = computed((): number => {
    if (!selectedInsurance.value) return 0;
    return getInsuranceTotalPrice(selectedInsurance.value);
  });

  function selectInsurance(id: string): void {
    selectedInsuranceId.value = selectedInsuranceId.value === id ? null : id;
  }

  async function fetchSscInsurances(): Promise<void> {
    if (
      !catalogCode.value ||
      !productCode.value ||
      // null = board code missing (block); '' = intentional no-meal (allow)
      boardCode.value === null ||
      !roomCodes.value ||
      !checkIn.value ||
      !checkOut.value ||
      !passengers.value
    ) {
      console.warn('[Checkout SSC] Cannot fetch: missing required parameters');
      return;
    }

    // Reset previous insurance selection (options may change with different extras)
    selectedInsuranceId.value = null;
    sscError.value = null;
    isLoadingSsc.value = true;

    try {
      const extras: SelectedExtra[] = [];
      for (const [bookingCodeId, serviceTypeCode] of selectedExtrasMap.value.entries()) {
        const service = ppsExtras.value.find((e) => e.BookingCodeId === bookingCodeId);
        if (!service) continue;
        extras.push({
          requirement: service.Requirement,
          bookingCode: service.BookingCode,
          serviceTypeCode,
          from: service.From,
          to: service.To,
        });
      }

      const request = buildSscInsurancesRequest({
        roomCodes: roomCodes.value,
        boardCode: boardCode.value,
        productCode: productCode.value,
        catalogCode: catalogCode.value,
        checkIn: checkIn.value,
        checkOut: checkOut.value,
        passengers: passengers.value,
        selectedExtras: extras,
      });

      const response = await getInsurances(request);
      sscInsurances.value = response.Insurances;
    } catch (error) {
      console.warn('[Checkout SSC] Failed to fetch insurance data:', error);
      sscError.value = error instanceof Error ? error.message : 'Failed to load insurance options';
      sscInsurances.value = [];
      // Do not throw - checkout continues without SSC data
    } finally {
      isLoadingSsc.value = false;
    }
  }

  function clearSscData(): void {
    sscInsurances.value = [];
    isLoadingSsc.value = false;
    sscError.value = null;
    selectedInsuranceId.value = null;
  }

  // Actions: Phase 2.5 - PPS Extras Data
  const hasPpsData = computed(() => ppsExtras.value.length > 0);

  const selectedExtrasPrice = computed((): number => {
    let total = 0;
    for (const [bookingCodeId, serviceTypeCode] of selectedExtrasMap.value.entries()) {
      const service = ppsExtras.value.find((e) => e.BookingCodeId === bookingCodeId);
      if (!service) continue;
      const st = service.ServiceTypes?.find((t) => t.Code === serviceTypeCode);
      if (st) {
        // Total is currently always 0 from PPS API; `||` falls back to Price (see pps/index.ts)
        total += st.Total || st.Price;
      }
    }
    return total;
  });

  function selectServiceType(bookingCodeId: number, serviceTypeCode: string): void {
    const newMap = new Map(selectedExtrasMap.value);
    if (newMap.get(bookingCodeId) === serviceTypeCode) {
      newMap.delete(bookingCodeId);
    } else {
      newMap.set(bookingCodeId, serviceTypeCode);
    }
    selectedExtrasMap.value = newMap;
  }

  // Paired selection used by transport inbound/outbound
  function selectPairedServiceType(
    inboundId: number,
    outboundId: number,
    serviceTypeCode: string
  ): void {
    const newMap = new Map(selectedExtrasMap.value);
    const bothSelected =
      newMap.get(inboundId) === serviceTypeCode && newMap.get(outboundId) === serviceTypeCode;
    if (bothSelected) {
      newMap.delete(inboundId);
      newMap.delete(outboundId);
    } else {
      newMap.set(inboundId, serviceTypeCode);
      newMap.set(outboundId, serviceTypeCode);
    }
    selectedExtrasMap.value = newMap;
  }

  function getSelectedPairedServiceTypeCode(
    inboundId: number,
    outboundId: number
  ): string | undefined {
    const a = selectedExtrasMap.value.get(inboundId);
    const b = selectedExtrasMap.value.get(outboundId);
    return a && a === b ? a : undefined;
  }

  async function fetchPpsExtras(): Promise<void> {
    if (
      !catalogCode.value ||
      !productCode.value ||
      !checkIn.value ||
      !checkOut.value ||
      !passengers.value
    ) {
      console.warn('[Checkout PPS] Cannot fetch: missing required parameters');
      return;
    }

    ppsError.value = null;
    isLoadingPps.value = true;

    try {
      const request = buildPpsExtrasRequest({
        catalogCode: catalogCode.value,
        productCode: productCode.value,
        checkIn: checkIn.value,
        checkOut: checkOut.value,
        nightCount: nightCount.value,
        adultCount: adultCount.value,
      });

      const response = await getExtras(request);
      ppsExtras.value = extractExtrasFromResponse(response);
    } catch (error) {
      console.warn('[Checkout PPS] Failed to fetch extras data:', error);
      ppsError.value = error instanceof Error ? error.message : 'Failed to load extras options';
      ppsExtras.value = [];
      // Do not throw - checkout continues without PPS data
    } finally {
      isLoadingPps.value = false;
    }
  }

  function clearPpsData(): void {
    ppsExtras.value = [];
    isLoadingPps.value = false;
    ppsError.value = null;
    selectedExtrasMap.value = new Map();
  }

  // Actions: Phase 4 - Traveler Data (Step 2)
  function setTravelerData(data: CheckoutFormData): void {
    travelerData.value = data;
  }

  function clearTravelerData(): void {
    travelerData.value = null;
  }

  function applyPreSubmitResult(params: {
    code: number;
    sessionId: string;
    activityId: string;
    bookingResponse: Record<string, unknown>;
  }): void {
    const combinationId = roomCombination.value?.products.map((p) => p.offerId).join('-') ?? '';

    captureFlowBeforeConfirmation(params.code);
    multiRoomAvailabilityResult.value = {
      code: params.code,
      message: '',
      available: isAvailableCode(params.code),
      combinationId,
      baStage: 'preSubmit',
      sessionId: params.sessionId,
      activityId: params.activityId,
      bookingResponse: params.bookingResponse,
    };

    sessionId.value = params.sessionId;
    activityId.value = params.activityId;

    extractAndStorePricing(params.bookingResponse);
    extractAndStoreDetails(params.bookingResponse);
  }

  function applyBookingFlowChange(newCode: number): void {
    if (!multiRoomAvailabilityResult.value) return;

    captureFlowBeforeConfirmation(newCode);
    multiRoomAvailabilityResult.value = {
      ...multiRoomAvailabilityResult.value,
      code: newCode,
      available: isAvailableCode(newCode),
    };
  }

  // Actions: Phase 5 - Booking Submission
  async function submitBooking(): Promise<BookingSubmitResult> {
    if (isSubmittingBooking.value) {
      return {
        success: false,
        returnCode: 999,
        error: $t(
          'ibe.checkout.booking.error.in-progress',
          'A booking submission is already in progress.'
        ),
      };
    }

    if (!sessionId.value || !activityId.value) {
      const errorResult: BookingSubmitResult = {
        success: false,
        returnCode: 999,
        error: $t(
          'ibe.checkout.booking.error.missing-session',
          'Missing session data. Please restart checkout.'
        ),
      };
      bookingError.value = errorResult.error!;
      return errorResult;
    }

    if (!availabilityCode.value) {
      const errorResult: BookingSubmitResult = {
        success: false,
        returnCode: 999,
        error: $t(
          'ibe.checkout.booking.error.missing-availability',
          'Missing availability code. Please restart checkout.'
        ),
      };
      bookingError.value = errorResult.error!;
      return errorResult;
    }

    if (!travelerData.value) {
      const errorResult: BookingSubmitResult = {
        success: false,
        returnCode: 999,
        error: $t(
          'ibe.checkout.booking.error.missing-traveler',
          'Missing traveler information. Please complete the form.'
        ),
      };
      bookingError.value = errorResult.error!;
      return errorResult;
    }

    if (!multiRoomAvailabilityResult.value?.bookingResponse) {
      const errorResult: BookingSubmitResult = {
        success: false,
        returnCode: 999,
        error: $t(
          'ibe.checkout.booking.error.missing-booking-data',
          'Missing booking data. Please restart checkout.'
        ),
      };
      bookingError.value = errorResult.error!;
      return errorResult;
    }

    // Clear previous state
    bookingError.value = null;
    bookingResult.value = null;
    isSubmittingBooking.value = true;

    try {
      const result = await submitBookingApi({
        sessionId: sessionId.value!,
        activityId: activityId.value!,
        bookingResponse: multiRoomAvailabilityResult.value!.bookingResponse!,
        availabilityCode: availabilityCode.value!,
        formData: travelerData.value,
        travelers: travelersSummary.value,
        travelType: travelType.value ?? undefined,
        agencyNumber: agencyNumber.value ?? undefined,
      });

      bookingResult.value = result;

      if (!result.success && !result.flowChanged) {
        bookingError.value =
          result.error || $t('ibe.checkout.booking.error.failed', 'Booking failed');
        console.warn('[Checkout] Booking failed:', result);
      }

      return result;
    } catch (error) {
      console.error('[Checkout] Booking error:', error);

      const errorResult: BookingSubmitResult = {
        success: false,
        returnCode: 999,
        error:
          error instanceof Error
            ? error.message
            : $t('ibe.checkout.booking.error.unknown', 'Unknown error during booking'),
      };
      bookingResult.value = errorResult;
      bookingError.value = errorResult.error!;
      return errorResult;
    } finally {
      isSubmittingBooking.value = false;
    }
  }

  function clearBookingResult(): void {
    bookingResult.value = null;
    bookingError.value = null;
    isSubmittingBooking.value = false;
  }

  // Computed: Phase 4 - Travelers Summary
  const travelersSummary = computed((): Traveler[] => {
    if (!travelerData.value) return [];

    const result: Traveler[] = [];
    const { contactPerson, invoiceRecipient, travelers } = travelerData.value;

    // If contact person is traveling ('solo'), add them as first traveler.
    // In solo mode, salutation and birthDate are validated as required by the form.
    if (invoiceRecipient === 'solo') {
      result.push({
        id: 'contact',
        type: 'adult',
        salutation: contactPerson.salutation,
        firstName: contactPerson.firstName,
        lastName: contactPerson.lastName,
        birthDate: contactPerson.birthDate!,
        street: contactPerson.street,
        postalCode: contactPerson.postalCode,
        location: contactPerson.location,
        country: contactPerson.country,
        nationality: contactPerson.nationality,
        mobile: contactPerson.mobile,
        email: contactPerson.email,
      });
    }

    // Add other travelers from the array. `traveler.type` is already the 4-way variant
    // (derived in getFormData via deriveVariant), and it narrows the stored union so the
    // adult branch can read salutation/address while the gender-style branch reads gender.
    travelers.forEach((traveler) => {
      if (traveler.type === 'adult') {
        result.push({
          id: traveler.id,
          type: 'adult',
          salutation: traveler.salutation,
          firstName: traveler.firstName,
          lastName: traveler.lastName,
          birthDate: traveler.birthDate,
          street: traveler.street,
          postalCode: traveler.postalCode,
          location: traveler.location,
          country: traveler.country,
          nationality: traveler.nationality,
          mobile: traveler.mobile,
          email: traveler.email,
        });
      } else {
        // Gender-style variants (under18adult / child / infant) - simplified fields.
        result.push({
          id: traveler.id,
          type: traveler.type,
          gender: traveler.gender,
          firstName: traveler.firstName,
          lastName: traveler.lastName,
          birthDate: traveler.birthDate,
          nationality: traveler.nationality,
        });
      }
    });

    return result;
  });

  // Computed: Phase 5 - Booking Submission
  const hasBookingResult = computed(() => bookingResult.value !== null);
  const bookingNumber = computed(() => bookingResult.value?.bookingNumber ?? null);
  const isBookingSuccess = computed(() => bookingResult.value?.success ?? false);

  return {
    activeStep,
    activeSubStep: readonly(activeSubStep),
    offerIds: readonly(offerIds),
    roomCodes: readonly(roomCodes),
    serviceVariantIds: readonly(serviceVariantIds),
    productCode,
    boardCode,
    checkIn,
    checkOut,
    passengers,
    roomOccupancy,
    roomCombination,
    isMultiRoom,
    totalRooms,

    // Phase 2 - Availability Check
    multiRoomAvailabilityResult: readonly(multiRoomAvailabilityResult),
    isCheckingAvailability,
    availabilityError,
    sessionId,
    activityId,
    travelType: readonly(travelType),
    ageBands: readonly(ageBands),
    isLoadingAgeBands: readonly(isLoadingAgeBands),
    agencyNumber,
    agencyNumberIsFallback,
    servicePrices: readonly(servicePrices),
    totalBookingPrice: readonly(totalBookingPrice),
    roomOnlyPrices: readonly(roomOnlyPrices),
    boardNames: readonly(boardNames),
    boardDescriptions: readonly(boardDescriptions),
    separateItems: readonly(separateItems),
    auxServices: readonly(auxServices),
    accommodationTotal: readonly(accommodationTotal),
    paymentSchedule,
    mealDescription: readonly(mealDescription),
    occupationConstraints: readonly(occupationConstraints),
    popupHints: readonly(popupHints),
    agencyName: readonly(agencyName),
    tourOperator: readonly(tourOperator),
    discounts: readonly(discounts),
    totalDiscount: readonly(totalDiscount),
    originalPrice: readonly(originalPrice),
    rebateSummary: readonly(rebateSummary),
    discountReasons,
    discountPercent: readonly(discountPercent),
    hasDiscount,

    // Phase 2.5: SSC Insurance State
    catalogCode: readonly(catalogCode),
    sscInsurances: readonly(sscInsurances),
    isLoadingSsc: readonly(isLoadingSsc),
    sscError: readonly(sscError),
    hasSscData,
    selectedInsuranceId: readonly(selectedInsuranceId),
    selectedInsurance,
    selectedInsurancePrice,

    // Phase 2.5: PPS Extras State
    ppsExtras: readonly(ppsExtras),
    isLoadingPps: readonly(isLoadingPps),
    ppsError: readonly(ppsError),
    hasPpsData,
    selectedExtrasMap: readonly(selectedExtrasMap),
    selectedExtrasPrice,

    // Phase 3: NBC State
    nbcProductData: readonly(nbcProductData),
    isLoadingNbc: readonly(isLoadingNbc),
    nbcError: readonly(nbcError),

    // Phase 4: Traveler Data
    travelerData: readonly(travelerData),
    travelersSummary,

    // Phase 5: Booking Submission State
    bookingResult: readonly(bookingResult),
    isSubmittingBooking: readonly(isSubmittingBooking),
    bookingError: readonly(bookingError),

    // Computed validation
    hasProductId,
    hasProductCode,
    hasRoomCode,
    hasBoardCode,
    hasServiceVariantId,
    hasCheckIn,
    hasCheckOut,
    hasPassengers,
    hasValidDateRange,
    hasAllRequiredParams,
    missingRequiredFields,
    roomsMissingAdult,
    firstMissingParam,

    // Computed: data summary
    adultCount,
    childCount,
    infantCount,
    totalPassengers,
    checkInFormatted,
    checkOutFormatted,
    nightCount,

    // Computed: Phase 2 Availability
    hasCheckedAvailability,
    isAvailable,
    availabilityCode,
    availabilityMessage,
    requiresRequestBooking,
    requiresConfirmation,
    isFixedBookingType,
    flowBeforeConfirmation: readonly(flowBeforeConfirmation),
    effectivePaymentFlow,
    isPaymentMethodVisible,
    isRequestSubmitCta,
    canProceedToNextStep,
    isVamosTourOperator,

    // Computed: Phase 3 NBC
    hasNbcData,
    productHighlights,
    productDescription,
    productName,
    destinationName,
    mainProductImage,
    productDetailUrl,
    providerTermsLink,
    cancellationTermsLink,
    isPackage,
    vviUrl,
    isStatutoryCheckboxVisible,

    // Computed: Phase 5 Booking Submission
    hasBookingResult,
    bookingNumber,
    isBookingSuccess,

    // Actions: State Management
    setInitialState,
    setActiveStep,
    setActiveSubStep,
    clearCheckout,

    // Actions: Validation
    validateCheckout,
    getErrorMessageForParam,

    // Actions: Phase 2 Availability
    checkProductAvailability,
    fetchAgeBands,
    retryAvailabilityCheck,
    clearAvailability,

    // Actions: Phase 2.5 SSC
    fetchSscInsurances,
    selectInsurance,
    clearSscData,

    // Actions: Phase 2.5 PPS
    fetchPpsExtras,
    selectServiceType,
    selectPairedServiceType,
    getSelectedPairedServiceTypeCode,
    clearPpsData,

    // Actions: Phase 3 NBC
    fetchNbcProductData,
    clearNbcData,

    // Actions: Phase 4 Traveler Data
    setTravelerData,
    clearTravelerData,
    applyPreSubmitResult,

    // Actions: Phase 5 Booking Submission
    submitBooking,
    applyBookingFlowChange,
    clearBookingResult,

    // Debug mode
    isDebugMode: readonly(isDebugMode),
  };
});
